// Costing service: per-department ingredient & recipe costing with Firestore persistence
// Follows the same Firestore-first pattern used in store.js

import * as Sentry from '@sentry/react'
import { db } from './firebase'
import { collection, doc, setDoc, getDoc, getDocs, deleteDoc, query, where, orderBy } from 'firebase/firestore'

// ─── Helpers ───

function genId() {
  return crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).slice(2)
}

function now() {
  return new Date().toISOString()
}

// ─── Firestore collection paths ───

function ingredientsCol(departmentId) {
  return collection(db, 'departments', departmentId, 'costing_ingredients')
}

function ingredientDoc(departmentId, ingredientId) {
  return doc(db, 'departments', departmentId, 'costing_ingredients', ingredientId)
}

function recipesCol(departmentId) {
  return collection(db, 'departments', departmentId, 'costing_recipes')
}

function recipeDoc(departmentId, recipeId) {
  return doc(db, 'departments', departmentId, 'costing_recipes', recipeId)
}

// ─── Calculation Helpers ───

/**
 * Calculate cost per usable unit, accounting for waste.
 * Formula: (purchaseCost / usableQtyPerUnit) / (1 - wastePercent / 100)
 */
export function calcCostPerUsableUnit(purchaseCost, usableQtyPerUnit, wastePercent = 0) {
  const cost = Number(purchaseCost) || 0
  const qty = Number(usableQtyPerUnit) || 1
  const waste = Number(wastePercent) || 0

  if (qty === 0) return 0
  const wasteFactor = 1 - waste / 100
  if (wasteFactor <= 0) return 0

  return (cost / qty) / wasteFactor
}

/**
 * Calculate recipe totals from ingredient list.
 * Returns { totalCost, costPerPortion, sellingPrice, grossMargin }
 */
export function calcRecipeTotals(ingredients = [], portions = 1, targetMargin = 0) {
  const totalCost = ingredients.reduce((sum, ing) => sum + (Number(ing.cost) || 0), 0)
  const portionCount = Number(portions) || 1
  const margin = Number(targetMargin) || 0

  const costPerPortion = totalCost / portionCount

  let sellingPrice = 0
  let grossMargin = 0
  if (margin > 0 && margin < 100) {
    sellingPrice = costPerPortion / (1 - margin / 100)
    grossMargin = sellingPrice - costPerPortion
  }

  return {
    totalCost: Math.round(totalCost * 100) / 100,
    costPerPortion: Math.round(costPerPortion * 100) / 100,
    sellingPrice: Math.round(sellingPrice * 100) / 100,
    grossMargin: Math.round(grossMargin * 100) / 100,
  }
}

/**
 * Bulk breakdown: calculate per-unit and daily costs for bulk purchases.
 * Returns { totalUnits, costPerUnit, dailyCost, dailyUnits }
 */
export function calcBulkBreakdown(packSize, numPacks, totalCost, usageDays) {
  const size = Number(packSize) || 0
  const packs = Number(numPacks) || 0
  const cost = Number(totalCost) || 0
  const days = Number(usageDays) || 1

  const totalUnits = size * packs
  const costPerUnit = totalUnits > 0 ? cost / totalUnits : 0
  const dailyUnits = totalUnits / days
  const dailyCost = cost / days

  return {
    totalUnits: Math.round(totalUnits * 100) / 100,
    costPerUnit: Math.round(costPerUnit * 100) / 100,
    dailyCost: Math.round(dailyCost * 100) / 100,
    dailyUnits: Math.round(dailyUnits * 100) / 100,
  }
}

// ─── Ingredients CRUD ───

/**
 * Get all ingredients for a department.
 */
export async function getIngredients(departmentId) {
  try {
    const snapshot = await getDocs(ingredientsCol(departmentId))
    const ingredients = []
    snapshot.forEach(d => ingredients.push(d.data()))
    return ingredients
  } catch (err) {
    console.warn('Failed to fetch ingredients:', err.message)
    Sentry.captureException(err, { extra: { module: 'costingService', fn: 'getIngredients', departmentId } })
    return []
  }
}

/**
 * Save an ingredient (create or update).
 * Auto-generates ID for new ingredients, adds timestamps.
 */
export async function saveIngredient(departmentId, ingredient) {
  const isNew = !ingredient.id
  const id = ingredient.id || genId()
  const timestamp = now()

  const costPerUsableUnit = calcCostPerUsableUnit(
    ingredient.purchaseCost,
    ingredient.usableQtyPerUnit,
    ingredient.wastePercent
  )

  const record = {
    ...ingredient,
    id,
    costPerUsableUnit,
    updatedAt: timestamp,
    createdAt: isNew ? timestamp : (ingredient.createdAt || timestamp),
  }

  try {
    await setDoc(ingredientDoc(departmentId, id), record)
  } catch (err) {
    console.warn('Failed to save ingredient:', err.message)
    Sentry.captureException(err, { extra: { module: 'costingService', fn: 'saveIngredient', departmentId, id } })
  }

  return record
}

/**
 * Delete an ingredient by ID.
 */
export async function deleteIngredient(departmentId, ingredientId) {
  try {
    await deleteDoc(ingredientDoc(departmentId, ingredientId))
    return true
  } catch (err) {
    console.warn('Failed to delete ingredient:', err.message)
    Sentry.captureException(err, { extra: { module: 'costingService', fn: 'deleteIngredient', departmentId, ingredientId } })
    return false
  }
}

// ─── Recipes CRUD ───

/**
 * Get all recipes for a department.
 */
export async function getRecipes(departmentId) {
  try {
    const snapshot = await getDocs(recipesCol(departmentId))
    const recipes = []
    snapshot.forEach(d => recipes.push(d.data()))
    return recipes
  } catch (err) {
    console.warn('Failed to fetch recipes:', err.message)
    Sentry.captureException(err, { extra: { module: 'costingService', fn: 'getRecipes', departmentId } })
    return []
  }
}

/**
 * Save a recipe (create or update).
 * Auto-generates ID for new recipes, recalculates totals, adds timestamps.
 */
export async function saveRecipe(departmentId, recipe) {
  const isNew = !recipe.id
  const id = recipe.id || genId()
  const timestamp = now()

  const { totalCost, costPerPortion, sellingPrice, grossMargin } = calcRecipeTotals(
    recipe.ingredients,
    recipe.portions,
    recipe.targetMargin
  )

  const record = {
    ...recipe,
    id,
    totalCost,
    costPerPortion,
    sellingPrice,
    grossMargin,
    updatedAt: timestamp,
    createdAt: isNew ? timestamp : (recipe.createdAt || timestamp),
  }

  try {
    await setDoc(recipeDoc(departmentId, id), record)
  } catch (err) {
    console.warn('Failed to save recipe:', err.message)
    Sentry.captureException(err, { extra: { module: 'costingService', fn: 'saveRecipe', departmentId, id } })
  }

  return record
}

/**
 * Delete a recipe by ID.
 */
export async function deleteRecipe(departmentId, recipeId) {
  try {
    await deleteDoc(recipeDoc(departmentId, recipeId))
    return true
  } catch (err) {
    console.warn('Failed to delete recipe:', err.message)
    Sentry.captureException(err, { extra: { module: 'costingService', fn: 'deleteRecipe', departmentId, recipeId } })
    return false
  }
}
